import { useMemo } from 'react'

import { useCollaborationNotesPanel } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import {
  contentItemNoteSubject,
  postNoteSubject,
} from '~publish/hooks/notes/noteSubject'
import type {
  ContentItemNoteSubject,
  PostNoteSubject,
} from '~publish/hooks/notes/noteSubject'
import { useContentItemNotesEnabled } from '~publish/hooks/notes/useContentItemNotesEnabled'

import { useComposerMode } from '../store/appSlice/hooks'
import { useEditedPostId, useOmniDraftId } from '../store/postSlice/hooks'

/**
 * The thread the composer's Notes panel shows. A Post also names the
 * ContentItem it belongs to, whose closed draft thread shows above it; that
 * is `null` while ContentItem Notes are off or the Post has no ContentItem.
 */
export type ComposerNotesSubject =
  | (PostNoteSubject & { contentItemId: string | null })
  | ContentItemNoteSubject

/**
 * The Notes subject the composer mounts Notes for, or `null` when the
 * surface is off.
 *
 * - Editing a saved Post: the Post's thread.
 * - Editing a saved channel-less draft: the ContentItem's own thread, behind
 *   the ContentItem Notes split. Reopening a draft starts a create session
 *   seeded with its id in `omni.draftId`; saving a new draft never seeds it.
 * - Anything unsaved (a new post or a new draft): nothing. There is no id to
 *   read or write Notes against, and the composer adds neither a temporary
 *   id nor automatic saving to invent one (CORE-7507).
 */
export function useComposerNotesSubject(): ComposerNotesSubject | null {
  const mode = useComposerMode()
  const editedPostId = useEditedPostId()
  const omniDraftId = useOmniDraftId()
  const { showPanel } = useCollaborationNotesPanel()
  const contentItemNotesEnabled = useContentItemNotesEnabled()

  const postId =
    showPanel && mode === 'edit' && editedPostId ? editedPostId : null
  // A Post's session also seeds `omni.draftId` with its ContentItem, for the
  // title editor. Only a session with no Post edits the draft itself.
  const draftContentItemId =
    showPanel &&
    contentItemNotesEnabled &&
    mode === 'create' &&
    !editedPostId &&
    omniDraftId
      ? omniDraftId
      : null
  const postContentItemId =
    postId && contentItemNotesEnabled ? omniDraftId : null

  return useMemo(() => {
    if (postId) {
      return { ...postNoteSubject(postId), contentItemId: postContentItemId }
    }
    if (draftContentItemId) return contentItemNoteSubject(draftContentItemId)
    return null
  }, [draftContentItemId, postContentItemId, postId])
}
